import {
  SVButton,
  SVCheckbox,
  SVForm,
  SVSelect,
  SVTextInput,
} from '@/components/core';
import { REGEX } from '@/constants/regex';
import { TransferType } from '@/types/transactions';
import {
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MESSAGES } from '@/constants/messages';
import { logAnalyticsEvent } from '@/utils/analytics';
import showToast from '@/utils/toast';
import { Info } from '@mui/icons-material';
import { captureException } from '@sentry/browser';
import { convertToNumber } from '@/utils/currency';
import CreateTransactionConfirm from './CreateTransactionConfirm';
import CreateTransactionFooter from './CreateTransactionFooter';
import { useCreateTransactionRequest } from '@/hooks/requests/useTransactionRequests';
import {
  CreateTransactionRequest,
  TransactionPurpose,
  TransactionRequestSelectionItem,
} from '@/types/transactionRequests';
import { SelectOption } from '@/types/inputs';
import WireRecipientAddForm from '../wireRecipients/add/WireRecipientAddForm';
import DefaultLayout from '../layouts/DefaultLayout';
import { useAuthUser } from '@/hooks/stores/useAuthUser';

type TransactionFormProps = {
  customerId: string;
  confirmHeader: string;
  formHeader: string;
  toAccounts: TransactionRequestSelectionItem[];
  fromAccounts: TransactionRequestSelectionItem[];
  onSubmit: () => void;
  onCancel: () => void;
  preselectedToAccount?: string;
  preselectedFromAccount?: string;
  isWire?: boolean;
};

export default function TransactionForm({
  customerId,
  confirmHeader,
  formHeader,
  toAccounts,
  fromAccounts,
  onSubmit,
  onCancel,
  preselectedToAccount,
  preselectedFromAccount,
  isWire,
}: TransactionFormProps) {
  const { isStaff } = useAuthUser();
  const [openAddWireRecipientModal, setOpenAddWireRecipientModal] =
    useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isNonUsAccount, setIsNonUsAccount] = useState(false);

  const { createTransactionRequest, isCreateTransactionRequestPending } =
    useCreateTransactionRequest();

  const formMethods = useForm<CreateTransactionRequest>({
    defaultValues: {
      customerId,
      fromAccountId: preselectedFromAccount ? preselectedFromAccount : '',
      toAccountId: preselectedToAccount ? preselectedToAccount : '',
      amount: '',
      note: '',
      instructions: '',
      requestExceedingLimitException: false,
      skipCustomerReview: true,
      purpose: '',
    },
    mode: 'onBlur',
  });
  const [fromAccountId, toAccountId, amount, note, instructions, purpose] =
    formMethods.watch([
      'fromAccountId',
      'toAccountId',
      'amount',
      'note',
      'instructions',
      'purpose',
    ]);

  useEffect(() => {
    formMethods.resetField('toAccountId');
  }, [fromAccountId]);

  const fromAccount = fromAccounts.find(
    account => account.value === fromAccountId,
  );
  const isFromAccountExternal = !fromAccount?.isInternalAccount;

  const toAccount = toAccounts.find(account => account.value === toAccountId);
  const isToAccountExternal = !toAccount?.isInternalAccount;

  useEffect(() => {
    if (toAccountId) {
      if (toAccount?.bankAddressCountryCode !== 'US') {
        setIsNonUsAccount(true);
      } else {
        setIsNonUsAccount(false);
      }
    }
  }, [toAccountId]);

  const transactionType = isFromAccountExternal
    ? 'deposit'
    : isToAccountExternal
      ? 'withdrawal'
      : 'transfer';

  const filteredToAccounts = toAccounts.filter(account => {
    return isFromAccountExternal
      ? account.value !== fromAccountId && account.isInternalAccount
      : account.value !== fromAccountId;
  });

  const purposeOptions: SelectOption[] = Object.values(TransactionPurpose).map(
    purpose => ({
      label: purpose,
      value: purpose,
    }),
  );

  async function handleSubmit(data: CreateTransactionRequest) {
    try {
      const response = await createTransactionRequest({
        ...data,
        transactionType: isWire ? 'wire' : transactionType,
        speed: isWire ? TransferType.Wire : TransferType.Standard,
        skipCustomerReview: toAccount?.callerIsAuthorizedTeammate
          ? data.skipCustomerReview
          : false,
      });
      onSubmit();

      if (response.requiresStaffReview) {
        showToast({
          type: 'error',
          data: {
            content: isStaff
              ? 'Your wire request has been flagged for additional review. Go to the "Transaction Review" section in the Staff portal to view additional details.'
              : "As part of standard procedure, we're reviewing your wire request for security. A client concierge will be in touch shortly to assist you!",
          },
          autoClose: false,
        });
      } else if (response.requestExceedingLimitException) {
        showToast({
          type: 'error',
          data: {
            content:
              'Your request amount exceeds the limit. A client concierge will reach out to assist you.',
          },
          autoClose: false,
        });
      } else {
        showToast({
          type: 'success',
          data: {
            content: 'Successful request creation',
          },
        });
      }
    } catch (error: any) {
      captureException(error);
      logAnalyticsEvent({
        category: 'error',
        action: 'transferForm',
      });
      const errorMessage = error?.response?.data;
      if (errorMessage === 'invalid_balance') {
        showToast({
          type: 'error',
          data: { content: MESSAGES.exceedsBalance },
        });
        setIsConfirming(false);
        return;
      }

      showToast({
        type: 'error',
        data: { content: errorMessage || MESSAGES.genericError },
      });
    }
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h4" component="h1">
        {isConfirming ? confirmHeader : formHeader}
      </Typography>
      <SVForm<CreateTransactionRequest>
        methods={formMethods}
        onSubmit={isConfirming ? handleSubmit : () => setIsConfirming(true)}>
        {isConfirming ? (
          <CreateTransactionConfirm
            fromAccount={fromAccount}
            toAccount={toAccount}
            amount={amount || ''}
            note={note}
            instructions={instructions}
            purpose={purpose}
            isWire={isWire}
          />
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SVSelect
                breadcrumbLabel={`${formHeader} From Bank Account`}
                rules={{ required: true }}
                name="fromAccountId"
                label="From"
                options={fromAccounts}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SVSelect
                breadcrumbLabel={`${formHeader} To Bank Account`}
                disabled={!fromAccountId && !toAccountId}
                rules={{
                  required: true,
                }}
                name="toAccountId"
                label="To"
                options={filteredToAccounts}
              />
              {isWire && (
                <SVButton
                  onClick={() => setOpenAddWireRecipientModal(true)}
                  variant="text">
                  Set up a new wire recipient
                </SVButton>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SVTextInput
                htmlInputProps={{
                  inputMode: 'numeric',
                }}
                inputProps={{
                  startAdornment: <Typography>$</Typography>,
                }}
                rules={{
                  required: true,
                  pattern: {
                    regex: REGEX.CURRENCY_VALUE,
                    readablePatternName: 'format',
                  },
                  validate: (value: string) => {
                    if (
                      fromAccount?.isInternalAccount &&
                      convertToNumber(value) >
                        convertToNumber(`${fromAccount.availableBalance.value}`)
                    ) {
                      return 'The amount exceeds the available balance';
                    }
                    return;
                  },
                }}
                name="amount"
                label="Amount"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SVTextInput rules={{ maxLength: 50 }} name="note" label="Note" />
            </Grid>
            {isWire && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SVTextInput
                  rules={{ maxLength: 140 }}
                  name="instructions"
                  label="Instructions"
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={`Your recipient may ask you to include additional routing info with your wire. If you're sending a wire to an investment or brokerage account, use this field to include "for further credit" or "for the benefit of" instructions along with the account holder's name and account number. Example: "FFC: John Smith 12345678"`}
                          placement="top-start">
                          <IconButton>
                            <Info />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            {isNonUsAccount && isWire && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SVSelect
                  label="Purpose"
                  name="purpose"
                  options={purposeOptions}
                  rules={{ required: true }}
                />
              </Grid>
            )}
            {toAccount?.callerIsAuthorizedTeammate && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SVCheckbox
                  label="Skip Customer Review"
                  name="skipCustomerReview"
                />
              </Grid>
            )}
          </Grid>
        )}
        <CreateTransactionFooter
          isConfirming={isConfirming}
          setIsConfirming={setIsConfirming}
          isCreateTransactionPending={isCreateTransactionRequestPending}
          handleSubmit={handleSubmit}
          handleCancel={onCancel}
        />
      </SVForm>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={openAddWireRecipientModal}
        onClose={() => setOpenAddWireRecipientModal(false)}>
        <DialogContent>
          <DefaultLayout header="Add Wire Recipient">
            <WireRecipientAddForm
              customerId={customerId}
              onLeave={() => setOpenAddWireRecipientModal(false)}
              isModal
            />
          </DefaultLayout>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
